import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import type { Reference } from '@/lib/zybooks-api';
import { type Node as PMNode } from '@tiptap/pm/model';
import { NodeViewWrapper, useEditorState, type EditorStateSnapshot, type ReactNodeViewProps } from '@tiptap/react';
import { AlertTriangle } from 'lucide-react';
import { useCallback } from 'react';
import { useReferenceStore } from './reference-store';
import { buildResourceNumberLookup } from './reference-utils';

function findResourcePos(doc: PMNode, nodeId: string): number | null {
    let targetPos: number | null = null;

    doc.descendants((node, pos) => {
        if (node.attrs?.id === nodeId) {
            targetPos = pos;
            return false;
        }

        return true;
    });

    return targetPos;
}

function isBrokenReference(reference: Reference | null): boolean {
    return Boolean(!reference || !reference.destination || (reference.destination.node_id && !reference.destination.resource_number));
}

function brokenReferenceLabel(reference: Reference | null): string {
    if (!reference) {
        return 'Broken section reference';
    }

    if (!reference.destination || (reference.destination.node_id && !reference.destination.resource_number)) {
        return 'Broken resource reference';
    }

    return 'Broken reference';
}

function brokenReferenceText(reference: Reference | null): string {
    if (!reference) {
        return 'Broken section reference';
    }

    if (!reference.destination || (reference.destination.node_id && !reference.destination.resource_number)) {
        return `${reference.destination.chapter_number}.${reference.destination.section_number}`;
    }

    return 'Broken reference';
}

export function ReferenceNodeView(props: ReactNodeViewProps) {
    const reference_section_guid = props.editor.state.doc.attrs?.sectionId;
    const reference_node_id = props.node.attrs?.id;

    // Subscribe to the specific reference from the store; re-renders only when this reference changes.
    const storedResource = useReferenceStore(
        props.editor,
        useCallback(
            s => s.referencesByKey?.[reference_section_guid]?.[reference_node_id] ?? null,
            [reference_section_guid, reference_node_id],
        ),
    );

    const isHighlighted = useReferenceStore(
        props.editor,
        useCallback(s => s.highlightedReferenceNodeId === reference_node_id, [reference_node_id]),
    );

    const isPendingCreation = useReferenceStore(
        props.editor,
        useCallback(
            s => Boolean(s.pendingReferencesByKey?.[reference_section_guid]?.[reference_node_id]),
            [reference_section_guid, reference_node_id],
        ),
    );

    // Track document changes that affect resource numbering. Counting non-text
    // nodes is insufficient for replacements like imageUpload -> image where the
    // count is stable but numbering buckets change.
    const resourceNumberingSignature = useEditorState({
        editor: props.editor,
        selector: useCallback((ctx: EditorStateSnapshot<typeof props.editor>) => {
            const doc = ctx.editor?.state.doc;
            if (!doc) {
                return '';
            }

            const resourceNumberLookup = buildResourceNumberLookup(doc);
            return JSON.stringify(Object.entries(resourceNumberLookup));
        }, []), // eslint-disable-line react-hooks/exhaustive-deps
    });
    void resourceNumberingSignature;

    const destination = storedResource?.destination;

    // Keep newly inserted references visually empty while the create call is still pending.
    if (!storedResource && isPendingCreation) {
        return (
            <NodeViewWrapper
                as="span"
                contentEditable={false}
                data-type="reference"
                className="inline"
            />
        );
    }

    // Used to scroll to the reference when it's in the same document instead of opening as a new tab.
    const currentSectionId = props.editor.state.doc.attrs?.sectionId;
    const isCurrentDocumentDestination = !!destination?.section_guid && !!currentSectionId && destination.section_guid === currentSectionId;
    const destinationScrollTargetId = destination?.sub_node_id || destination?.node_id || null;
    const handleReferenceClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isCurrentDocumentDestination && destinationScrollTargetId) {
            const targetPos = findResourcePos(props.editor.state.doc, destinationScrollTargetId);
            if (targetPos != null) {
                event.preventDefault();
                props.editor.commands.scrollToPos(targetPos);
            }
        }
    };

    const shouldGlow = isHighlighted;

    // If this is a resource/sub-resource reference to something in the current section, dynamically calculate the resource number.
    // This is done because the document could be updated and the number may change.
    const destinationLookupId = destination?.sub_node_id || destination?.node_id || null;
    const effectiveResourceNumber =
        isCurrentDocumentDestination && destinationLookupId
            ? (buildResourceNumberLookup(props.editor.state.doc)[destinationLookupId] ?? null)
            : (destination?.resource_number ?? null);

    const isMissingCurrentDocumentDestination = isCurrentDocumentDestination && !!destination?.node_id && effectiveResourceNumber == null;

    if (isBrokenReference(storedResource) || isMissingCurrentDocumentDestination) {
        const label = isMissingCurrentDocumentDestination ? 'Broken resource reference' : brokenReferenceLabel(storedResource);
        const text = isMissingCurrentDocumentDestination
            ? `${destination?.chapter_number}.${destination?.section_number}`
            : brokenReferenceText(storedResource);

        return (
            <NodeViewWrapper
                as="span"
                contentEditable={false}
                data-type="reference"
                className="inline"
            >
                <span
                    className="inline-flex items-center gap-1 text-amber-700"
                    title={label}
                    aria-label={label}
                >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    <span>{text}</span>
                </span>
            </NodeViewWrapper>
        );
    }

    const resourcePrefix = typeof effectiveResourceNumber === 'number' ? '.' : '';
    const resourceNum = effectiveResourceNumber ? `${resourcePrefix}${String(effectiveResourceNumber)}` : '';
    const text = `${destination.chapter_number}.${destination.section_number}${resourceNum}`;
    const zybookCode = props.editor.state.doc.attrs?.zybookCode;
    const resourceQuery = destination.node_id ? `?nodeId=${encodeURIComponent(destination.node_id)}` : '';
    const resourceLink =
        destination?.ma_document_id != null && zybookCode
            ? `${window.location.origin}/books/${encodeURIComponent(zybookCode)}/${destination.ma_document_id}${resourceQuery}`
            : '';

    return (
        <ZyNodeViewWrapper
            as="span"
            contentEditable={false}
            data-type="reference"
            className="inline"
        >
            <a
                href={resourceLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleReferenceClick}
                data-reference-node-id={reference_node_id ?? undefined}
                className={`text-primary underline rounded-sm px-1 ${shouldGlow ? 'bg-primary/10 ring-2 ring-primary/70 shadow-[0_0_0_3px_rgba(59,130,246,0.25),0_0_0_8px_rgba(251,191,36,0.35),0_0_1.25rem_rgba(251,191,36,0.55)]' : ''}`}
            >
                {text}
            </a>
        </ZyNodeViewWrapper>
    );
}
